import apiClient from '@/shared/services/apiClient'

// Event types matching backend API
export type EventType =
  | 'QUESTION_OPEN'
  | 'QUESTION_CLOSE'
  | 'ANSWER_SELECT'
  | 'ANSWER_CLEAR'
  | 'MARK_FOR_REVIEW'
  | 'UNMARK_FOR_REVIEW'

// Question event structure matching QuestionEventRequest in API spec
export interface QuestionEventRequest {
  testQuestionId: string
  questionId: string
  eventType: EventType
  eventTimestamp: string
  selectedOption?: string // For ANSWER_SELECT events
  metadata?: Record<string, unknown> // Additional event-specific data
}

// Internal event with client-generated ID for deduplication
interface QueuedEvent extends QuestionEventRequest {
  _clientEventId: string // Used for local deduplication, not sent to server
}

// Request to send to backend
export interface RecordEventsRequest {
  events: QuestionEventRequest[]
}

// Configuration from environment variables
const BATCH_INTERVAL_SECONDS = Number(import.meta.env.VITE_EVENT_BATCH_INTERVAL_SECONDS) || 5
const BATCH_SIZE = Number(import.meta.env.VITE_EVENT_BATCH_SIZE) || 20
const MAX_RETRY_ATTEMPTS = 3
const INITIAL_RETRY_DELAY_MS = 1000
const STORAGE_KEY = 'test_events_queue'

class EventTrackingService {
  private eventQueue: QueuedEvent[] = []
  private attemptId: string | null = null
  private batchTimer: ReturnType<typeof setTimeout> | null = null
  private isSending = false
  private retryCount = 0
  private isOnline = navigator.onLine
  private clientEventCounter = 0

  constructor() {
    // Listen for online/offline events
    window.addEventListener('online', this.handleOnline)
    window.addEventListener('offline', this.handleOffline)

    // Handle page unload - send remaining events
    window.addEventListener('beforeunload', this.handleBeforeUnload)
    window.addEventListener('visibilitychange', this.handleVisibilityChange)

    // Load any persisted events from storage
    this.loadPersistedEvents()
  }

  /**
   * Generate a unique client event ID for deduplication
   */
  private generateClientEventId(): string {
    return `${Date.now()}-${++this.clientEventCounter}`
  }

  /**
   * Initialize tracking for a test attempt
   */
  initialize(attemptId: string): void {
    this.attemptId = attemptId
    this.startBatchTimer()
  }

  /**
   * Stop tracking and flush remaining events
   */
  async cleanup(): Promise<void> {
    this.stopBatchTimer()
    await this.flushEvents()
    this.attemptId = null
  }

  /**
   * Track a question event
   */
  private trackEvent(
    eventType: EventType,
    questionId: string,
    testQuestionId: string,
    options?: {
      selectedOption?: string
      metadata?: Record<string, unknown>
    }
  ): void {
    if (!this.attemptId) {
      // Silently skip - this can happen during React's render cycle before initialization
      return
    }

    const event: QueuedEvent = {
      _clientEventId: this.generateClientEventId(),
      testQuestionId,
      questionId,
      eventType,
      eventTimestamp: new Date().toISOString(),
      ...(options?.selectedOption && { selectedOption: options.selectedOption }),
      ...(options?.metadata && Object.keys(options.metadata).length > 0 && { metadata: options.metadata }),
    }

    this.eventQueue.push(event)
    this.persistEvents()

    // Check if we should send immediately
    if (this.eventQueue.length >= BATCH_SIZE) {
      this.flushEvents()
    }
  }

  /**
   * Track QUESTION_OPEN event
   */
  trackQuestionOpen(questionId: string, testQuestionId: string): void {
    this.trackEvent('QUESTION_OPEN', questionId, testQuestionId)
  }

  /**
   * Track QUESTION_CLOSE event
   * Backend will calculate totalTimeMs, visitCount from OPEN/CLOSE events
   */
  trackQuestionClose(questionId: string, testQuestionId: string): void {
    this.trackEvent('QUESTION_CLOSE', questionId, testQuestionId)
  }

  /**
   * Track ANSWER_SELECT event
   * Backend will calculate answerChangeCount from ANSWER_SELECT/ANSWER_CLEAR events
   */
  trackAnswerSelect(
    questionId: string,
    testQuestionId: string,
    selectedOption: string
  ): void {
    this.trackEvent('ANSWER_SELECT', questionId, testQuestionId, { selectedOption })
  }

  /**
   * Track ANSWER_CLEAR event
   */
  trackAnswerClear(questionId: string, testQuestionId: string): void {
    this.trackEvent('ANSWER_CLEAR', questionId, testQuestionId)
  }

  /**
   * Track MARK_FOR_REVIEW event
   */
  trackMarkForReview(questionId: string, testQuestionId: string): void {
    this.trackEvent('MARK_FOR_REVIEW', questionId, testQuestionId)
  }

  /**
   * Track UNMARK_FOR_REVIEW event
   */
  trackUnmarkForReview(questionId: string, testQuestionId: string): void {
    this.trackEvent('UNMARK_FOR_REVIEW', questionId, testQuestionId)
  }

  /**
   * Flush events to the server
   */
  async flushEvents(): Promise<void> {
    if (this.eventQueue.length === 0 || !this.attemptId || this.isSending) {
      return
    }

    if (!this.isOnline) {
      console.log('Offline - events will be sent when connection is restored')
      return
    }

    this.isSending = true
    const eventsToSend = [...this.eventQueue]

    try {
      await this.sendEvents(eventsToSend)

      // Remove sent events from queue
      const sentIds = new Set(eventsToSend.map((e) => e._clientEventId))
      this.eventQueue = this.eventQueue.filter((e) => !sentIds.has(e._clientEventId))
      this.persistEvents()
      this.retryCount = 0
    } catch (error) {
      console.error('Failed to send events:', error)
      await this.handleSendError()
    } finally {
      this.isSending = false
    }
  }

  /**
   * Send events to backend
   */
  private async sendEvents(events: QueuedEvent[]): Promise<void> {
    if (!this.attemptId || events.length === 0) return

    // Strip internal _clientEventId before sending
    const apiEvents: QuestionEventRequest[] = events.map(
      ({ _clientEventId, ...event }) => event
    )

    const request: RecordEventsRequest = { events: apiEvents }

    await apiClient.post(
      `/api/v1/test-attempts/${this.attemptId}/events`,
      request
    )
  }

  /**
   * Handle send error with exponential backoff
   */
  private async handleSendError(): Promise<void> {
    if (this.retryCount < MAX_RETRY_ATTEMPTS) {
      this.retryCount++
      const delay = INITIAL_RETRY_DELAY_MS * Math.pow(2, this.retryCount - 1)
      console.log(`Retrying in ${delay}ms (attempt ${this.retryCount})`)

      await new Promise((resolve) => setTimeout(resolve, delay))

      if (this.isOnline) {
        this.flushEvents()
      }
    } else {
      console.error('Max retry attempts reached. Events will be retried later.')
      this.retryCount = 0
    }
  }

  /**
   * Start the batch timer
   */
  private startBatchTimer(): void {
    this.stopBatchTimer()
    this.batchTimer = setInterval(() => {
      this.flushEvents()
    }, BATCH_INTERVAL_SECONDS * 1000)
  }

  /**
   * Stop the batch timer
   */
  private stopBatchTimer(): void {
    if (this.batchTimer) {
      clearInterval(this.batchTimer)
      this.batchTimer = null
    }
  }

  /**
   * Persist events to localStorage for offline support
   */
  private persistEvents(): void {
    try {
      const data = {
        attemptId: this.attemptId,
        events: this.eventQueue,
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
    } catch (error) {
      console.warn('Failed to persist events:', error)
    }
  }

  /**
   * Load persisted events from localStorage
   */
  private loadPersistedEvents(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const data = JSON.parse(stored)
        if (data.events && Array.isArray(data.events)) {
          this.eventQueue = data.events
          if (data.attemptId) {
            this.attemptId = data.attemptId
          }
        }
      }
    } catch (error) {
      console.warn('Failed to load persisted events:', error)
    }
  }

  /**
   * Clear persisted events
   */
  private clearPersistedEvents(): void {
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch (error) {
      console.warn('Failed to clear persisted events:', error)
    }
  }

  /**
   * Handle coming online
   */
  private handleOnline = (): void => {
    this.isOnline = true
    console.log('Back online - flushing queued events')
    this.flushEvents()
  }

  /**
   * Handle going offline
   */
  private handleOffline = (): void => {
    this.isOnline = false
    console.log('Gone offline - events will be queued')
  }

  /**
   * Handle page unload - use sendBeacon for reliability
   */
  private handleBeforeUnload = (): void => {
    if (this.eventQueue.length === 0 || !this.attemptId) return

    // Strip internal _clientEventId before sending
    const apiEvents: QuestionEventRequest[] = this.eventQueue.map(
      ({ _clientEventId, ...event }) => event
    )

    // Use sendBeacon for reliable delivery on page unload
    const url = `${apiClient.defaults.baseURL}/api/v1/test-attempts/${this.attemptId}/events`
    const data = JSON.stringify({ events: apiEvents })

    try {
      navigator.sendBeacon(url, new Blob([data], { type: 'application/json' }))
      this.clearPersistedEvents()
    } catch (error) {
      console.warn('sendBeacon failed:', error)
      // Events are already persisted, will be sent on next session
    }
  }

  /**
   * Handle visibility change - flush when tab becomes hidden
   */
  private handleVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') {
      this.flushEvents()
    }
  }

  /**
   * Get current queue size (for debugging)
   */
  getQueueSize(): number {
    return this.eventQueue.length
  }

  /**
   * Check if the service is initialized
   */
  isInitialized(): boolean {
    return !!this.attemptId
  }

  /**
   * Check if online
   */
  getIsOnline(): boolean {
    return this.isOnline
  }

  /**
   * Destroy the service and remove event listeners
   */
  destroy(): void {
    this.stopBatchTimer()
    window.removeEventListener('online', this.handleOnline)
    window.removeEventListener('offline', this.handleOffline)
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
    window.removeEventListener('visibilitychange', this.handleVisibilityChange)
  }
}

// Export singleton instance
export const eventTrackingService = new EventTrackingService()
export default eventTrackingService

// Expose methods globally for E2E testing
// This allows tests to force-flush events before navigation
if (typeof window !== 'undefined') {
  const win = window as unknown as {
    __flushTestEvents?: () => Promise<void>
    __getEventQueueSize?: () => number
  }
  win.__flushTestEvents = () => eventTrackingService.flushEvents()
  win.__getEventQueueSize = () => eventTrackingService.getQueueSize()
}
